import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot } from "@fortawesome/free-solid-svg-icons";

import {
  DATASET_SELECT_CAPABILITY,
  declaresCapability,
  installsIntoAccount,
  NODE_BUILD_CAPABILITY,
  SKILL_USE_WORDS,
  skillsUsedBy,
  type AgentAttachment,
  type AgentCardPart,
  type AgentChangePart,
  type AgentDatasetCandidatesPart,
  type AgentDatasetPick,
  type AgentDatasetSelection,
  type AgentDelegationPart,
  type AgentProposalPart,
  type AgentSessionTurn,
  type AgentSolveAttemptsPart,
  type RunStatusDisplay,
} from "../../../../services/agents";
import { AgentChangeCard } from "../../content/AgentChangeCard";
import { AgentChatCard } from "../../content/AgentChatCard";
import { AgentDatasetCandidatesCard } from "../../content/AgentDatasetCandidatesCard";
import { AgentSolveAttemptsCard } from "../../content/AgentSolveAttemptsCard";
import { AgentDelegationEntry } from "../../content/AgentDelegationEntry";
import { AgentReviewCard } from "../../content/AgentReviewCard";
import { SafeAgentContent } from "../../content/SafeAgentContent";
import { AgentRunStatusLine } from "../AgentRunStatusLine";
import { LlmConfigAction } from "../../../llmConfigs/LlmConfigAction";
import styles from "../AgentChatPanel.module.css";
import { lastingToolFailures, planNodeStateFor } from "./chatPanelDerived";

/** The callbacks a turn's cards may need: an install's review, a change's
 * Undo, a landed plan's rows. */
export interface AgentTurnActions {
  onComposePrompt: (prompt: string) => void;
  /** A link the agent wrote to another Curio page: the owner decides whether leaving the dataflow needs a confirmation first. */
  onInternalLink?: (to: string) => void;
  onOpenAgentChat?: (attachmentId: string) => void;
  delegateExists?: (attachmentId: string) => boolean;
  /** The name of the agent another attachment binds, from the live list. */
  agentNameOf?: (attachmentId: string) => string | null;
  onRecordDatasetSelection?: (picks: AgentDatasetPick[]) => Promise<AgentDatasetSelection>;
  onImportDataset?: (
    file: File,
    discoverySource?: import("../../../../services/datasetCatalog/datasetCatalogTypes").DatasetDiscoverySourceInput,
  ) => Promise<string | null>;
  onApplyProposal?: (proposalId: string) => Promise<unknown>;
  /** dev/84: a package.install proposal applies THROUGH the install review dialog. */
  beginPackageReview: (proposalId: string, dirName: string) => Promise<unknown>;
  onDismissProposal?: (proposalId: string) => Promise<void>;
  /** Undo a change this attachment's agent made. */
  onUndoChange?: (changeId: string) => Promise<unknown>;
  onSolvePlanNode?: (ref: string) => Promise<void>;
  onRunPlanNode?: (ref: string) => Promise<void>;
  /** An invited editor's chat: an install waits for the owner's review. */
  installsWaitForOwner?: boolean;
}

/**
 * One agent turn (split out of `AgentChatPanel.tsx` by memo dev/142, F4):
 * the avatar, the message, every typed content part it carries, the lines
 * that stay under it, and its meta line. Agent rich content renders ONLY
 * through the safe renderer (REQ-SEC-002); error markers and the lines under
 * the reply are plain text.
 */
export const AgentTurn: React.FC<{
  turn: AgentSessionTurn;
  attachment: AgentAttachment;
  tint: string;
  meta: RunStatusDisplay | null;
  actions: AgentTurnActions;
  /** A canvas agent's reply that changed nothing on the canvas (#243). */
  canvasUnchanged?: boolean;
}> = ({ turn: t, attachment, tint, meta, actions, canvasUnchanged = false }) => {
  // An agent on a node that records the dataset the node loads.
  const isFinderOnNode =
    declaresCapability(attachment, DATASET_SELECT_CAPABILITY) && attachment.target.kind === "node";
  // #447: the failed tool calls that keep their reason under the reply.
  const failedCalls = lastingToolFailures(t);
  const usedSkills = t.error ? [] : skillsUsedBy(t.execution);
  // An install on an invited editor's chat is the owner's to review.
  const waitsForOwner = (part: AgentProposalPart) =>
    Boolean(actions.installsWaitForOwner) && installsIntoAccount(part.tool);
  return (
    <div className={styles.agentRow}>
      <span className={`${styles.agentRowAvatar} ${tint}`} aria-hidden="true">
        <FontAwesomeIcon icon={faRobot} />
      </span>
      <div className={styles.agentCol}>
        <div className={`${styles.msgAgent} ${t.error ? styles.msgError : ""}`}>
          {t.error ? t.text : <SafeAgentContent text={t.text} onInternalLink={actions.onInternalLink} />}
          {t.error ? <LlmConfigAction remedy={t.remedy} /> : null}
          {(t.content ?? [])
            .filter((p): p is AgentCardPart => p.type === "card")
            .map((card, j) => (
              <AgentChatCard key={j} card={card} tintClassName={tint} />
            ))}
          {(t.content ?? [])
            .filter((p): p is AgentDatasetCandidatesPart => p.type === "datasetCandidates")
            .map((part, j) => (
              <AgentDatasetCandidatesCard
                key={`cand-${j}`}
                part={part}
                tintClassName={tint}
                onComposePrompt={actions.onComposePrompt}
                // dev/114: in the chat of an agent that builds nodes, the
                // runtime minted these from a dataset.discover delegation, so
                // the confirmation asks it to build.
                variant={declaresCapability(attachment, NODE_BUILD_CAPABILITY) ? "builder" : "finder"}
                // Any other agent hands a source to the agent it hands nodes
                // to build to, named as that agent's manifest names it.
                handoffName={attachment.nodeBuildDelegate}
                // dev/126: an agent that records selections, attached to a
                // NODE, records the confirmed source for it, which the node's
                // next Solve reads.
                onRecordSelection={
                  actions.onRecordDatasetSelection && isFinderOnNode ? actions.onRecordDatasetSelection : undefined
                }
                // dev/132: and the Import button under a portal row's download
                // steps — the same catalog import as the drawer footer.
                onImportDataset={actions.onImportDataset && isFinderOnNode ? actions.onImportDataset : undefined}
              />
            ))}
          {(t.content ?? [])
            .filter((p): p is AgentSolveAttemptsPart => p.type === "solveAttempts")
            .map((part, j) => (
              // dev/127: every attempt to fix this node, with the code it ran.
              <AgentSolveAttemptsCard
                key={`attempts-${part.nodeId}-${j}`}
                part={part}
                tintClassName={tint}
                onOpenChat={actions.onOpenAgentChat}
                agentName={part.attachmentId ? actions.agentNameOf?.(part.attachmentId) ?? null : null}
              />
            ))}
          {(t.content ?? [])
            .filter((p): p is AgentDelegationPart => p.type === "delegation")
            .map((part, j) => (
              <AgentDelegationEntry
                key={`dlg-${j}`}
                part={part}
                onOpenChat={actions.onOpenAgentChat}
                delegateExists={actions.delegateExists}
              />
            ))}
          {(t.content ?? [])
            .filter((p): p is AgentChangePart => p.type === "change")
            .map((part, j) => (
              <AgentChangeCard
                key={part.changeId ?? j}
                part={part}
                tintClassName={tint}
                onUndo={actions.onUndoChange}
                planNodeState={planNodeStateFor(attachment, part)}
                onSolvePlanNode={actions.onSolvePlanNode}
                onRunPlanNode={actions.onRunPlanNode}
                onOpenAgentChat={actions.onOpenAgentChat}
                delegateExists={actions.delegateExists}
                agentNameOf={actions.agentNameOf}
              />
            ))}
          {(t.content ?? [])
            .filter((p): p is AgentProposalPart => p.type === "proposal")
            .map((part, j) => (
              <AgentReviewCard
                key={part.proposalId ?? j}
                part={part}
                tintClassName={tint}
                waitsForOwner={waitsForOwner(part)}
                onApply={
                  waitsForOwner(part)
                    ? undefined
                    : part.tool === "package.install" && actions.onApplyProposal
                      ? (proposalId) => actions.beginPackageReview(proposalId, part.pins?.dirName ?? "")
                      : actions.onApplyProposal
                }
                onDismiss={waitsForOwner(part) ? undefined : actions.onDismissProposal}
              />
            ))}
        </div>
        {failedCalls.map((call, j) => (
          <div key={`call-${j}`} className={styles.turnNote}>
            {call.tool} · {call.reason}
          </div>
        ))}
        {canvasUnchanged ? <div className={styles.turnNote}>No changes were made to the canvas</div> : null}
        {/* The skills this reply's run held or loaded, from its record: each
            name says on hover how the run came to hold it. */}
        {usedSkills.length > 0 ? (
          <div className={`${styles.turnNote} ${styles.turnSkills}`} data-curio-reply-skills="true">
            Skills:{" "}
            {usedSkills.map((skill, j) => (
              <React.Fragment key={skill.name}>
                {j > 0 ? ", " : null}
                <span className={styles.turnSkill} data-tooltip={SKILL_USE_WORDS[skill.use]}>
                  {skill.name}
                </span>
              </React.Fragment>
            ))}
          </div>
        ) : null}
        {/* Per-reply execution status (dev/80 amendment): running while THIS
            reply streams, then its own duration + tokens. */}
        {meta ? (
          <div className={styles.turnMeta}>
            <AgentRunStatusLine display={meta} tintClassName={tint} />
          </div>
        ) : null}
      </div>
    </div>
  );
};
